import { buildDateAtTime, buildDateFromLocalParts, getLocalDateParts, getWeekDayForLocalDate, incrementLocalDate, toTimeParts, } from './date-timezone-utils';
const DEFAULT_END_OF_DAY_TIME = '17:00';
const MAX_BUSINESS_DAY_LOOKAHEAD = 14;
const DAYS_IN_WEEK = 7;
const MONDAY = 1;
const NINE_AM = '09:00';
const MINUTES_IN_DAY = 24 * 60;
const LAST_MINUTE_OF_DAY = MINUTES_IN_DAY - 1;
const MINUTE_MS = 60 * 1000;
function toMinutes(hour, minute) {
    return hour * 60 + minute;
}
function getTimeToMinutes(time) {
    const parts = toTimeParts(time);
    return parts ? toMinutes(parts.hour, parts.minute) : null;
}
/**
 * Window ends are exclusive, so a window closing at 23:59 (how schedules spell "until midnight")
 * runs to the end of the day rather than leaving its last minute uncovered.
 */
function getWindowEndMinutes(timeTo) {
    const endMinutes = getTimeToMinutes(timeTo);
    return endMinutes === LAST_MINUTE_OF_DAY ? MINUTES_IN_DAY : endMinutes;
}
export function isInHoliday(now, holidays) {
    return findActiveHoliday(now, holidays) !== null;
}
export function findActiveHoliday(now, holidays) {
    if (!holidays?.length) {
        return null;
    }
    const nowMs = now.getTime();
    return (holidays.find((holiday) => {
        const start = new Date(holiday.holidayStart).getTime();
        const end = new Date(holiday.holidayEnd).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
            return false;
        }
        return nowMs >= start && nowMs < end;
    }) ?? null);
}
export function isInTimeWindow(dateWindow, context) {
    const { weekDay, hour, minute } = getLocalDateParts(context.currentTime, context.timezone);
    if (dateWindow.weekDay !== weekDay) {
        return false;
    }
    const fromMinutes = getTimeToMinutes(dateWindow.timeFrom);
    const toWindowMinutes = getWindowEndMinutes(dateWindow.timeTo);
    if (fromMinutes === null || toWindowMinutes === null) {
        return false;
    }
    const currentMinutes = toMinutes(hour, minute);
    return currentMinutes >= fromMinutes && currentMinutes < toWindowMinutes;
}
export function isWithinBusinessHours(context) {
    if (isInHoliday(context.currentTime, context.holidays))
        return false;
    if (!context.businessHours?.length)
        return false;
    return context.businessHours.some((window) => isInTimeWindow(window, context));
}
/**
 * The escalation rules' `custom` time condition, shared with the support-policy Custom schedule
 * condition: any window containing the moment, except on a holiday.
 */
export function isInCustomSchedule(schedule, context) {
    if (!schedule?.length || isInHoliday(context.currentTime, context.holidays)) {
        return false;
    }
    return schedule.some((window) => isInTimeWindow(window, context));
}
export function hasRemainingBusinessHoursToday(context) {
    if (!context.businessHours?.length || isInHoliday(context.currentTime, context.holidays)) {
        return false;
    }
    const { hour, minute, weekDay } = getLocalDateParts(context.currentTime, context.timezone);
    const currentMinutes = toMinutes(hour, minute);
    return context.businessHours.some((window) => {
        if (window.weekDay !== weekDay) {
            return false;
        }
        const endMinutes = getWindowEndMinutes(window.timeTo);
        return endMinutes !== null && endMinutes > currentMinutes;
    });
}
function* iterateBusinessDays(from, context, options) {
    const hours = context.businessHours ?? [];
    const localFrom = getLocalDateParts(from, context.timezone);
    let candidate = {
        year: localFrom.year,
        month: localFrom.month,
        day: localFrom.day,
    };
    if (!options?.includeToday) {
        candidate = incrementLocalDate(candidate);
    }
    const lookaheadDays = options?.lookaheadDays ?? MAX_BUSINESS_DAY_LOOKAHEAD;
    for (let i = 0; i < lookaheadDays; i += 1) {
        const weekDay = getWeekDayForLocalDate(candidate, context.timezone);
        const dayWindows = hours.filter((window) => window.weekDay === weekDay);
        if (dayWindows.length) {
            const dayStart = buildDateFromLocalParts({
                ...candidate,
                hour: 0,
                minute: 0,
            }, context.timezone);
            if (options?.includeHolidayDays || !isInHoliday(dayStart, context.holidays)) {
                yield { candidate, dayWindows };
            }
        }
        candidate = incrementLocalDate(candidate);
    }
}
export function nextWeekdayAtTime(from, context, preferredTime) {
    const { weekDay } = context;
    if (typeof weekDay !== 'number' ||
        !Number.isInteger(weekDay) ||
        weekDay < 0 ||
        weekDay > 6) {
        return null;
    }
    const preferredTimeParts = toTimeParts(preferredTime);
    if (!preferredTimeParts) {
        return null;
    }
    const localFrom = getLocalDateParts(from, context.timezone);
    let daysUntil = (weekDay - localFrom.weekDay + DAYS_IN_WEEK) % DAYS_IN_WEEK;
    let candidate = {
        year: localFrom.year,
        month: localFrom.month,
        day: localFrom.day,
    };
    if (daysUntil === 0) {
        if (context.includeToday === false) {
            daysUntil = DAYS_IN_WEEK;
        }
        else {
            const todayAtTime = buildDateFromLocalParts({
                ...candidate,
                hour: preferredTimeParts.hour,
                minute: preferredTimeParts.minute,
            }, context.timezone);
            if (todayAtTime <= from) {
                daysUntil = DAYS_IN_WEEK;
            }
        }
    }
    for (let i = 0; i < daysUntil; i += 1) {
        candidate = incrementLocalDate(candidate);
    }
    for (let i = 0; i < MAX_BUSINESS_DAY_LOOKAHEAD; i += 1) {
        const deadline = buildDateFromLocalParts({
            ...candidate,
            hour: preferredTimeParts.hour,
            minute: preferredTimeParts.minute,
        }, context.timezone);
        if (!isInHoliday(deadline, context.holidays)) {
            return deadline;
        }
        candidate = incrementLocalDate(candidate);
    }
    return null;
}
export function nextMondayAtNine(from, timezone) {
    return nextWeekdayAtTime(from, {
        timezone,
        weekDay: MONDAY,
        includeToday: false,
    }, NINE_AM);
}
export function nextBusinessDay(from, context, preferredTime) {
    const preferredTimeParts = toTimeParts(preferredTime);
    if (!preferredTimeParts)
        return null;
    for (const { candidate } of iterateBusinessDays(from, context, { includeToday: false })) {
        return buildDateFromLocalParts({
            ...candidate,
            hour: preferredTimeParts.hour,
            minute: preferredTimeParts.minute,
        }, context.timezone);
    }
    return null;
}
/**
 * A 23:59 close holds through that minute, matching the window that runs to midnight
 * (`getWindowEndMinutes`); any other close holds up to its exact instant.
 */
function closesAfter(close, from, timezone) {
    const { hour, minute } = getLocalDateParts(close, timezone);
    const holdsUntil = toMinutes(hour, minute) === LAST_MINUTE_OF_DAY
        ? close.getTime() + MINUTE_MS
        : close.getTime();
    return holdsUntil >= from.getTime();
}
export function findBusinessDayEnd(from, context, fallbackTime = DEFAULT_END_OF_DAY_TIME) {
    for (const { candidate, dayWindows } of iterateBusinessDays(from, context, {
        includeToday: true,
    })) {
        const deadline = latestWindowEnd(candidate, dayWindows, context.timezone);
        // "at end of day" remains valid when the current time is exactly day end
        if (deadline && closesAfter(deadline, from, context.timezone)) {
            return deadline;
        }
    }
    return buildDateAtTime(from, fallbackTime, context.timezone);
}
/**
 * Close of business on the `count`th open day after the local date of `from`. That date never
 * counts, so a start before open, after close, or on a closed day is treated the same.
 */
export function findNthBusinessDayEnd(from, context, count) {
    if (!Number.isInteger(count) || count < 1) {
        return null;
    }
    let openDaysSeen = 0;
    for (const { candidate, dayWindows } of iterateBusinessDays(from, context, {
        // One open weekday per week is the sparsest schedule; the extra slack covers holidays.
        lookaheadDays: count * DAYS_IN_WEEK + MAX_BUSINESS_DAY_LOOKAHEAD,
    })) {
        const closeOfBusiness = latestWindowEnd(candidate, dayWindows, context.timezone);
        if (!closeOfBusiness) {
            continue;
        }
        openDaysSeen += 1;
        if (openDaysSeen === count) {
            return closeOfBusiness;
        }
    }
    return null;
}
export function findNextOpenTime(from, context) {
    for (const day of iterateBusinessDays(from, context, {
        includeToday: true,
        includeHolidayDays: true,
    })) {
        const openTime = findDayOpenTime(day, from, context);
        if (openTime) {
            return openTime;
        }
    }
    return null;
}
export function findNextScheduledOpening(from, context) {
    for (const { candidate, dayWindows } of iterateBusinessDays(from, context, {
        includeHolidayDays: true,
    })) {
        const openTime = earliestWindowStart(candidate, dayWindows, context.timezone);
        if (openTime) {
            return openTime;
        }
    }
    return null;
}
export function findOpenUntil(context) {
    if (!isWithinBusinessHours(context)) {
        return null;
    }
    const { currentTime, timezone } = context;
    const { year, month, day } = getLocalDateParts(currentTime, timezone);
    const candidate = { year, month, day };
    const weekDay = getWeekDayForLocalDate(candidate, timezone);
    const dayWindows = sortWindowsByStart((context.businessHours ?? []).filter((window) => window.weekDay === weekDay));
    let openUntil = null;
    for (const window of dayWindows) {
        const start = buildWindowBoundary(candidate, window.timeFrom, timezone);
        const end = buildWindowBoundary(candidate, window.timeTo, timezone);
        if (!start || !end) {
            continue;
        }
        if (!openUntil) {
            if (start <= currentTime && currentTime < end) {
                openUntil = end;
            }
            continue;
        }
        // Back-to-back windows (a split shift with no gap) are one uninterrupted opening.
        if (start <= openUntil && end > openUntil) {
            openUntil = end;
        }
    }
    if (!openUntil) {
        return null;
    }
    return findNextClosureStart(currentTime, openUntil, context.holidays) ?? openUntil;
}
function findNextClosureStart(from, until, holidays) {
    let earliest = null;
    for (const holiday of holidays ?? []) {
        const start = new Date(holiday.holidayStart);
        if (!Number.isFinite(start.getTime()) || start <= from || start >= until) {
            continue;
        }
        if (!earliest || start < earliest) {
            earliest = start;
        }
    }
    return earliest;
}
function findDayOpenTime({ candidate, dayWindows }, from, context) {
    for (const window of sortWindowsByStart(dayWindows)) {
        const start = buildWindowBoundary(candidate, window.timeFrom, context.timezone);
        const end = buildWindowBoundary(candidate, window.timeTo, context.timezone);
        if (!start || !end) {
            continue;
        }
        let openTime = start > from ? start : from;
        while (openTime < end) {
            const holiday = findActiveHoliday(openTime, context.holidays);
            if (!holiday) {
                return openTime;
            }
            const resumesAt = new Date(holiday.holidayEnd);
            if (!Number.isFinite(resumesAt.getTime())) {
                break;
            }
            openTime = resumesAt;
        }
    }
    return null;
}
function earliestWindowStart(candidate, dayWindows, timezone) {
    for (const window of sortWindowsByStart(dayWindows)) {
        const start = buildWindowBoundary(candidate, window.timeFrom, timezone);
        if (start) {
            return start;
        }
    }
    return null;
}
function latestWindowEnd(candidate, dayWindows, timezone) {
    let latest = null;
    for (const window of dayWindows) {
        const end = buildWindowBoundary(candidate, window.timeTo, timezone);
        if (end && (!latest || end > latest)) {
            latest = end;
        }
    }
    return latest;
}
function sortWindowsByStart(windows) {
    return [...windows].sort((left, right) => (getTimeToMinutes(left.timeFrom) ?? 0) - (getTimeToMinutes(right.timeFrom) ?? 0));
}
function buildWindowBoundary(candidate, time, timezone) {
    const parts = toTimeParts(time);
    if (!parts) {
        return null;
    }
    return buildDateFromLocalParts({ ...candidate, hour: parts.hour, minute: parts.minute }, timezone);
}
